import {
  Component, EventEmitter, Input, OnInit, Output, OnChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import UserGenerator from 'src/app/classes/user.class';
import { VerificationComponent } from 'src/app/components/verification/verification.component';
import { EntityService } from 'src/app/services/entity.service';

@Component({
  selector: 'app-user-general-info',
  templateUrl: './user-general-info.component.html',
  styleUrls: [ './user-general-info.component.scss' ],
})
export class UserGeneralInfoComponent implements OnInit {
  @Output() updateUserObject = new EventEmitter<void>();
  @Output() stepBack = new EventEmitter<void>();
  @Input() isEditMode: any;
  @Input() permissions: any;
  @Input() user: UserGenerator;
  @Input() roles: any;
  @Input() step: { text: string; completed: boolean; modified: boolean; }
  @Input() isAdminUser: boolean;

  formattedRoles: any;
  entityPrimaryEmail = '';

  constructor(public dialog: MatDialog,
    private entityService: EntityService) { }

  ngOnInit(): void {
    this.formatRoles();
    this.getEntityData();
  }

  getEntityData(): void {
    this.entityService.entityData$.subscribe((entityData: any) => {
      if (entityData) {
        this.entityPrimaryEmail = entityData.primaryEmail || '';
      }
    });
  }

  isAccountHolder(): boolean {
    return this.user?.contactEmail && this.entityPrimaryEmail &&
      this.user.contactEmail === this.entityPrimaryEmail;
  }

  isActiveFlagDisabled(): boolean {
    // Disable active flag editing if user is the account holder (has same email as entity primary email)
    return this.isAccountHolder() || !this.isEditMode || !this.permissions?.USER_EDIT;
  }

  formatRoles = () => {
    if (!this.roles) return;

    this.formattedRoles = this.roles?.map((role: any) => {
      return {
        text: role.roleName,
        value: role.id,
        accessLevel: role.accessLevel,
      }
    });
  }

  confirmRoleChangeManagementIssues = (selectedRoleId: number) => {
    const user = this.user;
    const newRole = this.formattedRoles?.find((role: any) => role.value == selectedRoleId);
    const newRoleAccessLevel = newRole?.accessLevel ?? '';

    const primaryManageKids = [ 'MANAGE_ALL_AUTHORITY', 'MANAGE_PRIMARY_AUTHORITY' ];
    const secondaryManageKids = [ 'MANAGE_SECONDARY_AUTHORITY' ];

    const hasPrimaryAccess = primaryManageKids.includes(newRoleAccessLevel);
    const managedPrimaryUsers = user.managedPrimaryUsers;
    const hasManagedPrimaryUsers = managedPrimaryUsers?.length > 0;
    const hasPrimaryIssue = !hasPrimaryAccess && hasManagedPrimaryUsers;

    const hasSecondaryAccess = hasPrimaryAccess || secondaryManageKids.includes(newRoleAccessLevel);
    const managedSecondaryUsers = user.managedSecondaryUsers;
    const hasManagedSecondaryUsers = managedSecondaryUsers?.length > 0;
    const hasSecondaryIssue = !hasSecondaryAccess && hasManagedSecondaryUsers;

    const hasBothIssues = hasPrimaryIssue && hasSecondaryIssue;

    let text = null;

    if (hasBothIssues) {
      text = `This user currently has ${managedPrimaryUsers?.length} primary and ${managedSecondaryUsers?.length} secondary management assignments. The requested role has a reduced access level and will remove all primary and secondary management assignments.`;
    } else if (hasPrimaryIssue) {
      text = `This user currently has ${managedPrimaryUsers?.length} primary management assignments. The requested role has a reduced access level and will remove the primary management assignments.`;
    } else if (hasSecondaryIssue) {
      text = `This user currently has ${managedSecondaryUsers?.length} secondary management assignments. The requested role has a reduced access level and will remove the secondary management assignments.`;
    }

    if (text) {
      const dialogRef = this.dialog.open(VerificationComponent, {
        data: {
          type: 'alert',
          title: 'Are you sure you want to change this user\'s role?',
          text,
          confirmButtonTitle: 'Yes, remove management assignments',
          cancelButtonTitle: 'No, keep current role',
          confirmButtonClass: 'red',
        },
      });

      dialogRef.afterClosed().subscribe((result: any) => {
        if (result === 'verified') {
          this.user.removeManagedAssignments = true;
          this.user.updateValue('roleIds', selectedRoleId);
        }
      });
    } else {
      this.user.updateValue('roleIds', selectedRoleId);
    }

  }

  confirmUserDeactivation = (newActiveFlag: number) => {
    const user = this.user;
    const managedPrimaryUsers = user.managedPrimaryUsers;
    const managedSecondaryUsers = user.managedSecondaryUsers;
    const hasManagedPrimaryUsers = managedPrimaryUsers?.length > 0;
    const hasManagedSecondaryUsers = managedSecondaryUsers?.length > 0;
    const hasBothIssues = hasManagedPrimaryUsers && hasManagedSecondaryUsers;

    let text = null;

    if (hasBothIssues) {
      text = `This user currently has ${managedPrimaryUsers?.length} primary and ${managedSecondaryUsers?.length} secondary management assignments. Deactivating this user will remove all primary and secondary management assignments.`;
    } else if (hasManagedPrimaryUsers) {
      text = `This user currently has ${managedPrimaryUsers?.length} primary management assignments. Deactivating this user will remove the primary management assignments.`;
    } else if (hasManagedSecondaryUsers) {
      text = `This user currently has ${managedSecondaryUsers?.length} secondary management assignments. Deactivating this user will remove the secondary management assignments.`;
    }

    if (text) {
      const dialogRef = this.dialog.open(VerificationComponent, {
        data: {
          type: 'alert',
          title: 'Are you sure you want to deactivate this user?',
          text,
          confirmButtonTitle: 'Yes, deactivate user',
          cancelButtonTitle: 'No, keep user active',
          confirmButtonClass: 'red',
        },
      });

      dialogRef.afterClosed().subscribe((result: any) => {
        if (result === 'verified') {
          this.user.updateValue('activeFlag', newActiveFlag);
          this.step.modified = true;
        }
      });
    } else {
      // No management assignments, proceed with deactivation
      this.user.updateValue('activeFlag', newActiveFlag);
      this.step.modified = true;
    }
  }

  updateUserParameter = (
    event: Event | any, parameter: keyof UserGenerator, type?: string,
  ) => {
    const eventValue = event?.target?.value;
    const value = eventValue !== undefined ? eventValue : event;

    if (parameter === 'roleIds') {
      return this.confirmRoleChangeManagementIssues(value);
    }

    if (parameter === 'activeFlag') {
      // Prevent account holders from changing their own active flag
      if (this.isAccountHolder()) {
        return;
      }
      const isActive = event?.target?.checked;
      const newActiveFlag = isActive ? 1 : 0;

      // If deactivating user (changing from active to inactive), show confirmation dialog
      if (this.user.activeFlag === 1 && newActiveFlag === 0) {
        return this.confirmUserDeactivation(newActiveFlag);
      }

      this.user.updateValue(parameter, newActiveFlag);
      this.step.modified = true;
      return;
    }

    this.user.updateValue(parameter, value);
    this.step.modified = true;
  }

  checkDisabled = () => {
    // This checks to make sure that email is an email and not anything else
    if (this.user.contactEmail) {
      const email = this.user.contactEmail;
      const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
      if (!emailPattern.test(email)) {
        return true
      }
    }
    return false
  }

  updateUser = () => {
    if (this.user.firstName === '' ||
      this.user.lastName === '' ||
      this.user.contactEmail === '' ||
      this.user?.roleIds?.length === 0) {
      return
    }
    this.updateUserObject.emit();
  }

  closeDialog = () => {
    this.dialog.closeAll();
  }

}
